// Better‑than‑FAANG+ adaptive ContextMenu: accessible, mobile‑aware, unified, animated.
// Backward compatible: old props supported (x,y, os, parent, fwiend, conversation,...)
import './contextMenu.css';
import ClearAllOutlinedIcon from '@mui/icons-material/ClearAllOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import axiosConn from '../../../axiosConn';
import { SocketContext } from '../../../context/socket';
import { Link } from 'react-router-dom';

export default function ContextMenu({
  x,
  y,
  anchorRect, // optional: DOMRect of triggering element (preferred)
  closeContextMenu,
  conversation,
  setMessages,
  os,
  setCurrentChat,
  setShowUnReadMessages,
  parent,
  fwiend,
}) {
  const { player } = useContext(AuthContext);
  const { socket: socket2 } = useContext(SocketContext);
  const menuRef = useRef(null);
  const [style, setStyle] = useState({});
  const [ready, setReady] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);

  const handleDeleteAllConversationMessages = useCallback(async () => {
    if (!conversation?._id) return;
    try {
      await axiosConn.delete('/messages/' + conversation._id, {
        headers: { Authorization: `Bearer ${player.token}` },
      });
    } catch {}
    setMessages?.([]);
    const receiverId = conversation?.members?.find(
      (m) => m !== player?.data?.player?._id,
    );
    try {
      socket2.emit('sendClearChat', {
        receiverId,
        conversationId: conversation._id,
      });
    } catch {}
    closeContextMenu?.();
  }, [conversation, player, socket2, setMessages, closeContextMenu]);

  // Build menu model
  const items = [];
  const notMessage = parent !== 'message';

  if (notMessage && os === 'iOS') {
    items.push({
      key: 'chat',
      label: 'Chat',
      icon: <SmsOutlinedIcon fontSize="small" />,
      onClick: () => {
        setCurrentChat?.(conversation);
        setShowUnReadMessages?.(false);
        closeContextMenu?.();
      },
    });
  }

  if (notMessage && fwiend?.playerId) {
    items.push({
      key: 'profile',
      label: 'Profile',
      icon: <PersonOutlineIcon fontSize="small" />,
      link: `/profile/${fwiend.playerId}`,
    });
  }

  if (notMessage) {
    items.push({
      key: 'clear',
      label: 'Clear',
      danger: true,
      icon: <ClearAllOutlinedIcon fontSize="small" />,
      onClick: handleDeleteAllConversationMessages,
    });
  }

  // Positioning logic (viewport safe)
  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let px = x ?? 0;
    let py = y ?? 0;

    if (anchorRect) {
      px = anchorRect.left;
      py = anchorRect.bottom + 6; // below anchor
    }

    // Wait for size
    const { offsetWidth: mw, offsetHeight: mh } = menu;
    if (px + mw > vw - 8) px = vw - mw - 8;
    if (py + mh > vh - 8) py = Math.max(8, vh - mh - 8);

    setStyle({
      top: `${py}px`,
      left: `${px}px`,
      '--cm-origin-x': anchorRect
        ? `${
            Math.min(
              Math.max((anchorRect.left + anchorRect.width / 2 - px) / mw, 0),
              1,
            ) * 100
          }%`
        : '20%',
      '--cm-origin-y': anchorRect
        ? `${
            Math.min(
              Math.max((anchorRect.top + anchorRect.height / 2 - py) / mh, 0),
              1,
            ) * 100
          }%`
        : '10%',
    });
    setReady(true);
  }, [x, y, anchorRect, items.length]);

  // Outside / escape close
  useEffect(() => {
    const onDown = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) {
        closeContextMenu?.();
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeContextMenu?.();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIdx((i) => (i + 1) % items.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIdx((i) => (i - 1 + items.length) % items.length);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const it = items[focusIdx];
        if (it) {
          if (it.link) {
            // let Link handle
            menuRef.current
              ?.querySelector(`[data-menu-index="${focusIdx}"] a`)
              ?.click();
          } else {
            it.onClick?.();
          }
        }
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [items, focusIdx, closeContextMenu]);

  // Focus management
  useEffect(() => {
    const btn = menuRef.current?.querySelector(
      `[data-menu-index="${focusIdx}"]`,
    );
    btn?.focus();
  }, [focusIdx, ready]);

  if (!items.length) return null;

  return (
    <div
      ref={menuRef}
      className={
        'contextmenu upgradedContextMenu ' + (ready ? 'cm-ready' : 'cm-enter')
      }
      style={style}
      role="menu"
      aria-label="Conversation options"
      tabIndex={-1}
      data-os={os}
    >
      {items.map((item, idx) => {
        const content = (
          <>
            <span className="cmIconWrap">{item.icon}</span>
            <span className="cmLabel">{item.label}</span>
          </>
        );
        return (
          <div
            key={item.key}
            role="menuitem"
            tabIndex={0}
            data-menu-index={idx}
            className={
              'cmItem' +
              (item.danger ? ' cmDanger' : '') +
              (focusIdx === idx ? ' cmFocus' : '')
            }
            onClick={() => {
              if (item.link) return; // Link wrapper handles navigation
              item.onClick?.();
            }}
            onMouseEnter={() => setFocusIdx(idx)}
          >
            {item.link ? <Link to={item.link}>{content}</Link> : content}
          </div>
        );
      })}
    </div>
  );
}
